import { useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useDisputes from 'api/useDisputes';
import useOrders from 'api/useOrders';
import { DisputeStatus } from 'common/contracts';
import { EToasterStatus, showToast } from 'utils/showToast';

import { ColumnList, ColumnListColumn } from 'components/ColumnList';
import { Order } from 'components/Order/types';
import { Button } from 'components/ui/button';

import { BASE_COLUMNS, MAX_EVIDENCE_COUNT } from './constants';
import { DisputeEvidenceConfirmationModal } from './DisputeEvidenceConfirmationModal';
import { DisputeEvidenceModal } from './DisputeEvidenceModal';
import { DisputeEvidenceEmptyState } from './EmptyState';
import { EvidenceActionMenu } from './EvidenceActionMenu';
import { EvidenceSelectionCheckbox } from './EvidenceSelectionCheckbox';

export const DisputeEvidence = ({
  order,
  publisherId,
  orderId
}: {
  order?: Order;
  publisherId: string;
  orderId: string;
}) => {
  const isDisputePassedSubmissionPhase = useMemo(() => {
    return [DisputeStatus.WON, DisputeStatus.LOST].includes(
      order?.dispute?.status as DisputeStatus
    );
  }, [order?.dispute?.status]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState<string>();
  const {
    submitEvidences,
    deleteDisputeEvidence,
    getSignedUrlForEvidenceFile
  } = useDisputes(order?.dispute?._id, publisherId);
  const { getOrderDetails } = useOrders(orderId, undefined, undefined, {
    selectedProjects: publisherId
  });
  const [selectedEvidences, setSelectedEvidences] = useState<string[]>([]);
  const handleSubmitEvidences = async () => {
    await submitEvidences.mutateAsync(selectedEvidences);
    showToast({
      message: 'Evidence submitted successfully',
      status: EToasterStatus.SUCCESS
    });
    getOrderDetails.refetch();
    setIsConfirmationModalOpen(false);
    setSelectedEvidences([]);
  };

  const handleDeleteEvidence = useCallback(
    async (evidenceId: string) => {
      await deleteDisputeEvidence.mutateAsync(evidenceId);
      showToast({
        message: 'Evidence deleted successfully',
        status: EToasterStatus.SUCCESS
      });
      setPopoverOpen(undefined);
      getOrderDetails.refetch();
    },
    [deleteDisputeEvidence, getOrderDetails]
  );

  const handleSelectionChange = useCallback(
    (evidenceId: string, checked: boolean) => {
      if (checked) {
        setSelectedEvidences((prev) => [...prev, evidenceId]);
      } else {
        setSelectedEvidences((prev) => prev.filter((id) => id !== evidenceId));
      }
    },
    []
  );

  const extractFileName = useCallback(
    (path: string | undefined): string | undefined => {
      if (!path) return;
      const parts = path.split('/');
      const lastPart = parts[parts.length - 1];
      const withoutExtension = lastPart.replace(/\.[^/.]+$/, '');
      const nameStart = withoutExtension.indexOf('-') + 1;
      return withoutExtension.slice(nameStart);
    },
    []
  );

  const handleDownload = useCallback(
    (filePath: string, evidenceId: string) => {
      if (!filePath || !evidenceId) return;

      getSignedUrlForEvidenceFile.mutate(
        {
          filePath,
          evidenceId
        },
        {
          onSuccess: (response: any) => {
            const signedUrls = response?.signedUrls || response;
            const url = Array.isArray(signedUrls) ? signedUrls[0] : signedUrls;
            if (url) {
              window.open(url, '_blank');
            } else {
              showToast({
                message: 'Error: No signed URL received',
                status: EToasterStatus.ERROR
              });
            }
          },
          onError: () => {
            showToast({
              message: 'Error downloading evidence',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    },
    [getSignedUrlForEvidenceFile]
  );

  const data = useMemo(() => {
    return (
      order?.dispute?.evidences?.map((evidence) => ({
        id: evidence._id,
        name: evidence.name,
        username: evidence.username,
        description: evidence.description,
        updatedAt: new Date(evidence.updatedAt).toLocaleString(),
        createdAt: new Date(evidence.createdAt).toLocaleString(),
        filePath: (evidence as any).filePath
      })) ?? []
    );
  }, [order?.dispute?.evidences]);

  const columns: ColumnListColumn[] = useMemo(() => {
    const baseColumnsWithFile = BASE_COLUMNS.map((col) => {
      if (col.accessor === 'file') {
        return {
          ...col,
          render: (value: any, row: any) => {
            const displayName = extractFileName(row.filePath);
            if (!displayName || !row.filePath) return null;
            return (
              <button
                onClick={() => handleDownload(row.filePath, row.id)}
                className="text-indigo-600 hover:text-indigo-800 underline cursor-pointer text-left"
              >
                {displayName}
              </button>
            );
          }
        };
      }
      return col;
    });

    if (!isDisputePassedSubmissionPhase && data?.length > 0) {
      return [
        {
          header: '',
          accessor: 'checkbox',
          align: 'left' as const,
          maxWidth: '50px',
          render: (_value: any, row: any) => (
            <EvidenceSelectionCheckbox
              evidenceId={row.id}
              selectedEvidences={selectedEvidences}
              onSelectionChange={handleSelectionChange}
            />
          )
        },
        ...baseColumnsWithFile,
        {
          header: '',
          accessor: 'actions',
          align: 'left' as const,
          maxWidth: '50px',
          render: (_value: any, row: any) => (
            <EvidenceActionMenu
              evidenceId={row.id}
              isOpen={popoverOpen === row.id}
              onOpen={() => setPopoverOpen(row.id)}
              onClose={() => setPopoverOpen(undefined)}
              onDelete={handleDeleteEvidence}
            />
          )
        }
      ];
    }

    return baseColumnsWithFile;
  }, [
    isDisputePassedSubmissionPhase,
    selectedEvidences,
    popoverOpen,
    handleSelectionChange,
    handleDeleteEvidence,
    data?.length,
    extractFileName,
    handleDownload
  ]);

  if (!order || !order.dispute) return null;
  const isMaxEvidenceReached = data.length >= MAX_EVIDENCE_COUNT;
  return (
    <>
      <DisputeEvidenceConfirmationModal
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        onConfirm={handleSubmitEvidences}
      />
      <DisputeEvidenceModal
        orderId={orderId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        order={order}
        publisherId={publisherId}
      />
      <div className="flex flex-col gap-2 p-4 pb-4 border border-base-sidebarBorder rounded-lg">
        <div className="flex items-center justify-between mb-2 pl-2 pb-2">
          <h2 className="font-medium text-xl text-base-primary">
            Dispute Evidence
          </h2>
          {!isDisputePassedSubmissionPhase && (
            <Button
              disabled={isMaxEvidenceReached}
              variant="default"
              className="flex items-center mt-4 gap-2 h-10 bg-indigo-700 text-white mr-4 disabled:opacity-60"
              onClick={() => setIsOpen(true)}
            >
              <FontAwesomeIcon icon="fa-regular fa-circle-plus !text-sm" />
              Add Evidence
            </Button>
          )}
        </div>
        {isMaxEvidenceReached && (
          <div className="text-xs text-neutral-500 pl-2 -mt-3 mb-1">
            Maximum of 5 evidence files reached
          </div>
        )}

        <ColumnList showColumns columns={columns} data={data} />

        {data.length === 0 && <DisputeEvidenceEmptyState />}

        {data.length > 0 && !isDisputePassedSubmissionPhase && (
          <div className="flex items-end justify-end mt-5">
            <Button
              disabled={
                submitEvidences.isLoading || selectedEvidences.length === 0
              }
              variant="default"
              className="flex items-center w-[300px] gap-2 h-10 bg-base-accent text-white text-base-foreground font-base"
              onClick={() => setIsConfirmationModalOpen(true)}
            >
              <FontAwesomeIcon icon="fa-regular fa-circle-check !text-sm" />
              Submit All
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
